import { useEffect, useState } from "react";
import { Plus, Trash2, Bell, BellOff, CalendarCheck } from "lucide-react";
import { api } from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";
import { nyFormat } from "@/lib/time";

export default function EconomicCalendar() {
    const [events, setEvents] = useState([]);
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState("all"); // all | high | medium | low | sample
    const [form, setForm] = useState({
        event: "", country: "US", importance: "high", scheduled_at: "",
        previous: "", forecast: "", actual: "", source_link: "",
    });

    const load = async () => {
        const { data } = await api.get("/economic-events");
        setEvents(data.sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at)));
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        if (!form.event || !form.scheduled_at) return;
        const iso = new Date(form.scheduled_at).toISOString();
        await api.post("/economic-events", { ...form, scheduled_at: iso, is_sample: false });
        setForm({ event: "", country: "US", importance: "high", scheduled_at: "", previous: "", forecast: "", actual: "", source_link: "" });
        setOpen(false);
        load();
    };

    const del = async (id) => { await api.delete(`/economic-events/${id}`); load(); };
    const toggleReminder = async (ev) => {
        await api.put(`/economic-events/${ev.id}`, { reminder: !ev.reminder });
        load();
    };
    const seed = async () => { await api.post("/economic-events/seed-samples"); load(); };

    const list = events.filter((e) => {
        if (filter === "all") return true;
        if (filter === "sample") return e.is_sample;
        return e.importance === filter;
    });

    return (
        <div className="space-y-8" data-testid="calendar-page">
            <PageHeader
                eyebrow="Mark the day with intention"
                title="Economic Calendar"
                subtitle="Manual entries now. Samples are clearly labeled and never shown as live. Prepared for a secure API source later."
                action={
                    <div className="flex gap-2 flex-wrap">
                        <button onClick={seed} className="px-3 py-2 text-sm border border-white/10 rounded-md hover:border-[#D4AF37]/40" data-testid="seed-samples-btn">
                            Load samples
                        </button>
                        <button onClick={() => setOpen(!open)} className="bg-[#D4AF37] text-[#0E0E10] px-3 py-2 rounded-md hover:bg-[#B3922E] text-sm inline-flex items-center gap-2" data-testid="event-new-btn">
                            <Plus className="w-4 h-4" /> New event
                        </button>
                    </div>
                }
            />

            <div className="flex gap-2 flex-wrap">
                {[
                    { id: "all", label: "All" },
                    { id: "high", label: "High" },
                    { id: "medium", label: "Medium" },
                    { id: "low", label: "Low" },
                    { id: "sample", label: "Samples" },
                ].map((f) => (
                    <button
                        key={f.id}
                        onClick={() => setFilter(f.id)}
                        data-testid={`filter-${f.id}`}
                        className={`px-3 py-1.5 rounded-md text-xs transition-colors ${
                            filter === f.id ? "bg-[#D4AF37] text-[#0E0E10]" : "text-zinc-400 border border-white/10 hover:border-white/20"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {open && (
                <form onSubmit={save} className="twp-card p-6 grid md:grid-cols-2 gap-3" data-testid="event-form">
                    <input placeholder="Event name" value={form.event} onChange={(e) => setForm({ ...form, event: e.target.value })} required className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="event-name" />
                    <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} required className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="event-time" />
                    <input placeholder="Country (US, EU, UK…)" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
                    <select value={form.importance} onChange={(e) => setForm({ ...form, importance: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm">
                        <option value="high">High importance</option>
                        <option value="medium">Medium importance</option>
                        <option value="low">Low importance</option>
                    </select>
                    <input placeholder="Previous" value={form.previous} onChange={(e) => setForm({ ...form, previous: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
                    <input placeholder="Forecast" value={form.forecast} onChange={(e) => setForm({ ...form, forecast: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
                    <input placeholder="Actual (when released)" value={form.actual} onChange={(e) => setForm({ ...form, actual: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
                    <input placeholder="Source link" value={form.source_link} onChange={(e) => setForm({ ...form, source_link: e.target.value })} className="bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
                    <button type="submit" className="bg-[#D4AF37] text-[#0E0E10] px-4 py-2 rounded-md hover:bg-[#B3922E] text-sm md:col-span-2" data-testid="event-save">Save event</button>
                </form>
            )}

            <section className="twp-card overflow-hidden" data-testid="events-list">
                <table className="w-full text-sm">
                    <thead className="text-left caption bg-[#0e0e10] border-b border-white/5">
                        <tr>
                            <th className="px-4 py-3">When (ET)</th>
                            <th className="px-4 py-3">Event</th>
                            <th className="px-4 py-3 hidden md:table-cell">Prev</th>
                            <th className="px-4 py-3 hidden md:table-cell">Fcst</th>
                            <th className="px-4 py-3 hidden md:table-cell">Actual</th>
                            <th className="px-4 py-3">Imp</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {list.map((ev) => (
                            <tr key={ev.id} data-testid={`event-row-${ev.id}`}>
                                <td className="px-4 py-3 font-mono text-xs text-zinc-400 whitespace-nowrap">{nyFormat(ev.scheduled_at)}</td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span>{ev.event}</span>
                                        <span className="text-xs text-zinc-500">· {ev.country}</span>
                                        {ev.is_sample && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 uppercase tracking-wider">Sample</span>}
                                    </div>
                                    {ev.source_link && <a href={ev.source_link} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#D4AF37] hover:underline">source ↗</a>}
                                </td>
                                <td className="px-4 py-3 hidden md:table-cell text-zinc-400">{ev.previous || "—"}</td>
                                <td className="px-4 py-3 hidden md:table-cell text-zinc-400">{ev.forecast || "—"}</td>
                                <td className="px-4 py-3 hidden md:table-cell font-mono gold">{ev.actual || "—"}</td>
                                <td className="px-4 py-3">
                                    <span className={`text-[10px] uppercase tracking-wider ${ev.importance === "high" ? "text-[#D4AF37]" : ev.importance === "medium" ? "text-zinc-300" : "text-zinc-500"}`}>{ev.importance}</span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                        <button onClick={() => toggleReminder(ev)} className="text-zinc-500 hover:text-[#D4AF37]" data-testid={`reminder-${ev.id}`} aria-label="Toggle reminder">
                                            {ev.reminder ? <Bell className="w-4 h-4" strokeWidth={1.5} /> : <BellOff className="w-4 h-4" strokeWidth={1.5} />}
                                        </button>
                                        <button onClick={() => del(ev.id)} className="text-zinc-500 hover:text-[#B95050]" aria-label="Delete event" data-testid={`del-event-${ev.id}`}>
                                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {list.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-4 py-10 text-center text-sm text-zinc-500">
                                    <CalendarCheck className="w-5 h-5 mx-auto mb-2 text-zinc-600" strokeWidth={1.5} />
                                    No events. Add one or load samples.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </section>

            <div className="text-xs text-zinc-500 leading-relaxed max-w-2xl">
                <strong className="text-zinc-300">Note.</strong> Sample events are clearly labeled and must never be mistaken for live
                data. A secure API source (e.g. Trading Economics or Finnhub) can be connected later via the backend.
            </div>
        </div>
    );
}
